/**
 * IndexedDB Cache Manager
 * Stores audio files locally for instant playback
 */

export interface CachedAudio {
  key: string;
  audioBlob: Blob;
  mappingUrl?: string;
  duration: number;
  timestamp: number;
  size: number;
}

export class CacheManager {
  private db: IDBDatabase | null = null;
  private unavailable = false;
  private initPromise: Promise<void> | null = null;
  private readonly dbName = 'readingvox-cache';
  private readonly dbVersion = 1;
  private readonly storeName = 'audio';
  private readonly maxSizeMB: number;

  constructor(maxSizeMB: number = 50) {
    this.maxSizeMB = maxSizeMB;
  }

  /**
   * Initialize IndexedDB
   */
  async init(): Promise<void> {
    if (this.db || this.unavailable) return; // Already initialized (or gave up)
    if (!this.initPromise) {
      this.initPromise = this.openDatabase().catch(() => {
        // IndexedDB blocked/unavailable (private mode, storage disabled, hung open):
        // run without the audio cache rather than breaking the widget.
        this.unavailable = true;
      });
    }
    return this.initPromise;
  }

  private openDatabase(): Promise<void> {
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => reject(new Error('IndexedDB open timed out')), 3000);
      let request: IDBOpenDBRequest;
      try {
        if (typeof indexedDB === 'undefined') throw new Error('IndexedDB unavailable');
        request = indexedDB.open(this.dbName, this.dbVersion);
      } catch (error) {
        clearTimeout(timer);
        reject(error);
        return;
      }

      request.onerror = () => {
        clearTimeout(timer);
        reject(new Error('Failed to open IndexedDB'));
      };

      request.onsuccess = () => {
        clearTimeout(timer);
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        // Create object store
        if (!db.objectStoreNames.contains(this.storeName)) {
          const store = db.createObjectStore(this.storeName, { keyPath: 'key' });
          store.createIndex('timestamp', 'timestamp', { unique: false });
          store.createIndex('size', 'size', { unique: false });
        }
      };
    });
  }

  /**
   * Save audio to cache
   */
  async save(
    key: string,
    audioBlob: Blob,
    mappingUrl?: string,
    duration: number = 0
  ): Promise<void> {
    await this.init();

    if (!this.db) return; // Cache unavailable — skip silently

    // Check cache size before saving
    await this.evictIfNeeded(audioBlob.size);

    const cacheEntry: CachedAudio = {
      key,
      audioBlob,
      mappingUrl,
      duration,
      timestamp: Date.now(),
      size: audioBlob.size
    };

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      const request = store.put(cacheEntry);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(new Error('Failed to save to cache'));
    });
  }

  /**
   * Get audio from cache
   */
  async get(key: string): Promise<CachedAudio | null> {
    await this.init();

    if (!this.db) return null; // Cache unavailable

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);
      const request = store.get(key);

      request.onsuccess = () => {
        const result = request.result as CachedAudio | undefined;

        if (result) {
          // Check if expired (30 days)
          const age = Date.now() - result.timestamp;
          const maxAge = 30 * 24 * 60 * 60 * 1000; // 30 days

          if (age > maxAge) {
            // Expired, delete it
            this.delete(key);
            resolve(null);
          } else {
            resolve(result);
          }
        } else {
          resolve(null);
        }
      };

      request.onerror = () => reject(new Error('Failed to get from cache'));
    });
  }

  /**
   * Delete audio from cache
   */
  async delete(key: string): Promise<void> {
    await this.init();

    if (!this.db) throw new Error('Database not initialized');

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      const request = store.delete(key);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(new Error('Failed to delete from cache'));
    });
  }

  /**
   * Get cache size
   */
  async getSize(): Promise<number> {
    await this.init();

    if (!this.db) throw new Error('Database not initialized');

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);
      const request = store.getAll();

      request.onsuccess = () => {
        const entries = request.result as CachedAudio[];
        const totalSize = entries.reduce((sum, entry) => sum + entry.size, 0);
        resolve(totalSize);
      };

      request.onerror = () => reject(new Error('Failed to get cache size'));
    });
  }

  /**
   * Evict old entries if cache is too large
   */
  private async evictIfNeeded(newItemSize: number): Promise<void> {
    const currentSize = await this.getSize();
    const maxSizeBytes = this.maxSizeMB * 1024 * 1024;

    if (currentSize + newItemSize > maxSizeBytes) {
      // Need to evict
      await this.evictOldest(currentSize + newItemSize - maxSizeBytes);
    }
  }

  /**
   * Evict oldest entries until we've freed enough space
   */
  private async evictOldest(bytesToFree: number): Promise<void> {
    await this.init();

    if (!this.db) throw new Error('Database not initialized');

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      const index = store.index('timestamp');
      const request = index.openCursor();

      let freedBytes = 0;

      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest).result as IDBCursorWithValue;

        if (cursor && freedBytes < bytesToFree) {
          const entry = cursor.value as CachedAudio;
          freedBytes += entry.size;
          cursor.delete();
          cursor.continue();
        } else {
          resolve();
        }
      };

      request.onerror = () => reject(new Error('Failed to evict cache entries'));
    });
  }

  /**
   * Clear all cache
   */
  async clear(): Promise<void> {
    await this.init();

    if (!this.db) throw new Error('Database not initialized');

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      const request = store.clear();

      request.onsuccess = () => resolve();
      request.onerror = () => reject(new Error('Failed to clear cache'));
    });
  }

  /**
   * Get cache statistics
   */
  async getStats(): Promise<{
    count: number;
    size: number;
    sizeMB: number;
    oldestTimestamp: number;
    newestTimestamp: number;
  }> {
    await this.init();

    if (!this.db) throw new Error('Database not initialized');

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);
      const request = store.getAll();

      request.onsuccess = () => {
        const entries = request.result as CachedAudio[];

        if (entries.length === 0) {
          resolve({
            count: 0,
            size: 0,
            sizeMB: 0,
            oldestTimestamp: 0,
            newestTimestamp: 0
          });
          return;
        }

        const totalSize = entries.reduce((sum, entry) => sum + entry.size, 0);
        const timestamps = entries.map(e => e.timestamp);

        resolve({
          count: entries.length,
          size: totalSize,
          sizeMB: totalSize / (1024 * 1024),
          oldestTimestamp: Math.min(...timestamps),
          newestTimestamp: Math.max(...timestamps)
        });
      };

      request.onerror = () => reject(new Error('Failed to get cache stats'));
    });
  }
}
